import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function OxfordshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Oxfordshire Pothole Claims | 157,000 Repairs in Five Years | Fixtyer"
        description="Oxfordshire County Council filled an estimated 157,000 potholes in five years and spends 46% above its DfT allocation, yet B and C roads hit an five-year high of 8% RED condition. See what the AMBER rating means for your claim."
        canonical="/councils/oxfordshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Oxfordshire', url: '/councils/oxfordshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Oxfordshire spends well above its DfT allocation with a GREEN spend rating — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road condition — and Oxfordshire is AMBER overall because B and C roads in RED condition have risen to 8%, the highest in five years. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 1,651km — 35% of Oxfordshire\'s 4,656km road network — and are surveyed over a four-year cycle in one direction across the full width. At the last survey, 19% of U-roads were in RED condition. That means any given stretch of residential road may not have been condition-surveyed for up to four years.',
          },
          {
            question: 'B and C roads hit 8% RED in 2024 — what does that mean for my claim?',
            answer: 'RED-condition B and C roads have risen from 6% in 2020 to 8% in 2024 — a third increase and the worst figure in Oxfordshire\'s published five-year data. B and C roads account for 2,408km, over half the network. If your pothole was on a classified local road, the council\'s own data shows deterioration is accelerating despite GREEN-rated spending.',
          },
          {
            question: 'Oxfordshire filled 37,042 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Filling potholes is reactive maintenance — evidence the network is producing defects faster than preventative treatment prevents them. Oxfordshire estimates roughly 30,000 potholes filled each year over the past five years, totalling 157,406. The council itself states reactive maintenance addresses issues "as they arise."',
          },
          {
            question: 'Does the four-year U-road survey cycle help my Section 58 argument?',
            answer: 'Yes. Oxfordshire surveys unclassified roads over four years, reporting a single RED percentage annually. A council relying on Section 58 must show a reasonable system for knowing road condition. On a network where 19% of U-roads need maintenance and each road is only surveyed once every four years, proving actual knowledge of your specific defect is harder for the council.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Oxfordshire County Council"
        councilSlug="oxfordshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/oxfordshire"
        headline="Oxfordshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Oxfordshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Oxfordshire: Green Spend, Declining Local Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Oxfordshire spends 46% above its DfT capital allocation and earns a GREEN scorecard for spend.
            Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — because B and C roads
            in RED condition have hit <strong className="text-rose-400">8%, the highest in five years</strong>,
            and the council filled an estimated <strong className="text-rose-400">157,406 potholes in five years</strong> while
            35% of its network is only condition-surveyed on a four-year cycle.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">157,406</div>
            <div className="text-white font-medium mt-2">Potholes filled in five years</div>
            <div className="text-brand-300 text-sm mt-1">
              37,042 in 2024/25 alone — roughly 101 potholes filled every day across
              Oxfordshire's 4,656km network, with about 60% of reactive spend going to pothole repairs.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Oxfordshire's own transparency report —
            classified roads slipping, unclassified roads still heavily degraded
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (597km — 12.8% of network): slowly declining</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">5%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 4% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">30%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 28% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">65%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 68% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Principal A-roads are surveyed on a two-year cycle — half the carriageway width each year.
              Good-condition A-roads have fallen three percentage points since 2020, with amber roads rising in step.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (2,408km — 51.7% of network): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold">31%</td>
                    <td className="px-4 py-2 text-center font-bold">61%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads are up a third since 2020 (6% → 8%), and good-condition roads have
              fallen from 63% to 61%. Over half the network now includes more roads needing — or soon needing — maintenance
              than at any point in the published five-year record.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£33.5m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£48.8m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">71%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Oxfordshire spends <span className="text-emerald-400">46% above its DfT allocation</span> —
              and B/C roads are still deteriorating. The problem isn't the chequebook. The network is
              producing defects faster than even a GREEN-rated spend programme can hold condition steady.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,651km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            35% of the network is unclassified roads — surveyed over a four-year cycle using Coarse Visual Inspection
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Four-Year Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Oxfordshire's own report states U roads "are surveyed over 4 years, in one direction but
                across the entire width." That means any given stretch of residential or village road may not
                have been condition-surveyed for <strong>up to four years</strong> — longer than Buckinghamshire's
                alternate-year cycle.
              </p>
              <p className="text-surface-600 text-sm">
                At the latest survey, <strong>19% of U-roads were in RED condition</strong> — approximately
                314km of residential streets, estate roads and rural lanes where the council's own data says
                maintenance should be considered.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">CVI vs SCANNER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Unlike A, B and C roads surveyed by SCANNER laser technology, U-roads use Coarse Visual
                Inspection where "an accessor records condition while being driven." Deterioration types are
                combined to give a single RED percentage — a cruder measure on the roads most drivers use daily.
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, Oxfordshire will switch to the BSI PAS 2161 standard with five condition
                categories instead of three. Until then, the four-year CVI cycle is what Section 58 arguments
                must be measured against.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Oxfordshire's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and how many years into the four-year cycle was it?</li>
              <li>• If 19% of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• How does a CVI drive-by survey detect a pothole forming between four-year inspections?</li>
              <li>• Does a data-driven, risk-based approach actually reach your specific residential street?</li>
            </ul>
            <p className="text-white font-medium">
              A council can't claim detailed knowledge of a network it only measures once every four years
              — on the road type where nearly one in five kilometres already needs maintenance.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            157,406 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">32,281</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">28,373</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">23,651</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">36,059</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">37,042</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">157,406</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Reactive maintenance focuses on addressing issues as they arise, such as pothole repairs. Pothole repair is a significant part of our reactive maintenance efforts, with about 60% of this expenditure dedicated to filling potholes. Over the past five years, we have filled an estimated 30,000 potholes each year, ensuring our roads remain safe and accessible."
              source="Oxfordshire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~86 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Oxfordshire fills around 86 potholes per day. The 2024/25 figure
                of 37,042 is the highest in the published record — roughly 101 every day. A network producing
                potholes at that rate is, by definition, a network where defects routinely form between
                inspections — exactly the scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2025/26 Repair Target</h3>
              <p className="text-amber-800 text-sm">
                For 2025/26, Oxfordshire has committed "in the region of £3.8M in highways defect repair,
                which we estimate will repair 56,000 defects, of which 41,000 are potholes." That is a planned
                increase — evidence the council expects the defect rate to remain high, not fall.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Survey Methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Oxfordshire Inspects Its Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Different road classes, different survey cycles — from the council's own methodology section
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Survey method</th>
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Cycle</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A-roads (597km)</td>
                  <td className="px-6 py-4 text-surface-600">SCANNER laser survey</td>
                  <td className="px-6 py-4 text-surface-600">2-year cycle, 50% carriageway width per year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B-roads (part of 2,408km)</td>
                  <td className="px-6 py-4 text-surface-600">SCANNER laser survey</td>
                  <td className="px-6 py-4 text-surface-600">2-year cycle, 50% carriageway width per year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">C-roads (part of 2,408km)</td>
                  <td className="px-6 py-4 text-surface-600">SCANNER laser survey</td>
                  <td className="px-6 py-4 text-surface-600">3-year cycle, full width, one direction</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">U-roads (1,651km)</td>
                  <td className="px-6 py-4 text-surface-600">Coarse Visual Inspection (CVI)</td>
                  <td className="px-6 py-4 text-surface-600">4-year cycle, full width, one direction</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Our goal is to increase the proportion of spend on preventative maintenance, as this is more cost-effective and sustainable in the long run. By investing more in preventative measures, we can reduce the frequency and severity of reactive repairs, ultimately leading to a more reliable and efficient road network."
              source="Oxfordshire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Preventative Spend Rising — Condition Isn't</h3>
              <p className="text-surface-600 text-sm mb-3">
                Oxfordshire's estimated preventative maintenance share has risen from 56% in 2022/23 to 71%
                in 2025/26. Reactive maintenance sits at just 7%. Yet B/C RED condition has still climbed to 8%.
              </p>
              <p className="text-surface-600 text-sm">
                The council's own stated goal — reducing reactive repairs through preventative investment —
                has not yet translated into improving classified road condition. That gap between intention and
                outcome is claim-relevant.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Rural Network Factor</h3>
              <p className="text-amber-800 text-sm">
                Oxfordshire notes that "around 50% of these roads being rural." Rural roads face heavier
                agricultural and weather-related wear, yet U-roads — many of them rural lanes — receive the
                longest survey gap and the crudest inspection method.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Oxfordshire invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend 46% above DfT allocation (£48.8m vs £33.5m)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 71% of maintenance spend estimated as preventative in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Data-driven asset management with Pavement Management System
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition improving (24% → 19% over five years)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Nearly £40m committed to capital maintenance in 2025/26 including 136 miles of preventative treatment
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads. Generic claims will struggle on well-surveyed routes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C roads in RED at an five-year high of 8%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — not the top tier of DfT compliance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 35% of the network surveyed only on a four-year CVI cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 157,406 potholes filled in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 60% of reactive spend goes to potholes — the council knows the problem is endemic
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend scorecards, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the four-year CVI survey gap is your strongest structural argument</li>
              <li>• Whether the road is rural — half the network is, yet receives the longest inspection intervals</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Oxfordshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Oxfordshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road four-year survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No B/C 8% RED deterioration cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline to 8% RED documented</li>
                <li>✅ Four-year U-road CVI survey gap argued</li>
                <li>✅ 157,406 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Oxfordshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=oxfordshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Oxfordshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Oxfordshire spends well above its DfT allocation with a GREEN spend rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is
                road condition — and Oxfordshire is AMBER overall because B and C roads in RED condition
                have risen to 8%, the highest in five years. Section 58 turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired, not on how much the council
                spends in aggregate.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,651km — 35% of Oxfordshire's 4,656km road network — and are surveyed over
                a four-year cycle in one direction across the full width. At the last survey, 19% of U-roads
                were in RED condition. That means any given stretch of residential road may not have been
                condition-surveyed for up to four years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                B and C roads hit 8% RED in 2024 — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                RED-condition B and C roads have risen from 6% in 2020 to 8% in 2024 — a third increase
                and the worst figure in Oxfordshire's published five-year data. B and C roads account for
                2,408km, over half the network. If your pothole was on a classified local road, the council's
                own data shows deterioration is accelerating despite GREEN-rated spending.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Oxfordshire filled 37,042 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Filling potholes is reactive maintenance — evidence the network is producing defects
                faster than preventative treatment prevents them. Oxfordshire estimates roughly 30,000 potholes
                filled each year over the past five years, totalling 157,406. The council itself states reactive
                maintenance addresses issues "as they arise."
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the four-year U-road survey cycle help my Section 58 argument?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Oxfordshire surveys unclassified roads over four years, reporting a single RED percentage
                annually. A council relying on Section 58 must show a reasonable system for knowing road
                condition. On a network where 19% of U-roads need maintenance and each road is only surveyed
                once every four years, proving actual knowledge of your specific defect is harder for the council.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.oxfordshire.gov.uk/transport-and-travel/transport-policies-and-plans/highway-maintenance/highways-maintenance-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Oxfordshire County Council Local Highways Maintenance Transparency Report 2025
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
